// RatingPills (DESIGN §3.9.5): Bewertungs-Pill-Reihe in einer Kachel `tile` (76 hoch, Radius 16),
// Label `label-strong` links (mobil darüber), Pills 26 hoch mit Trefferfläche 40/44. Aus `pill-tile`,
// an `accent`. Marker der Bar-Stufe: 2-px-Unterstrich `chart-bar-line` + Tooltip „Talent bar: …“.
// `input` = Radix RadioGroup (←/→ wählt), `computed` = nur lesend mit der Stufe aus der Engine.
import { useEffect, useRef, type ReactNode } from 'react';
import { ArrowDownIcon, ArrowUpIcon } from '@phosphor-icons/react';
import { RadioGroup } from 'radix-ui';
import { scrollChildIntoView } from './_lib/useScrollFade';
import { Tooltip } from './Tooltip';
import { cn } from '../basis/kern';

export interface RatingOption {
  value: string;
  label: string;
}

/** Stufen des Talent-Frameworks (Playbook S. 16) und Noten (S. 18). */
export const LEVEL_OPTIONS: RatingOption[] = [
  { value: 'poor', label: 'Poor' },
  { value: 'basic', label: 'Basic' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'exceptional', label: 'Exceptional' },
];
export const GRADE_OPTIONS: RatingOption[] = [
  { value: 'underperformer', label: 'Underperformer' },
  { value: 'above_bar', label: 'Above bar' },
  { value: 'a_player', label: 'A-player' },
];

export interface RatingPillsProps {
  /** Name der Reihe (Skill, Wert, Komponente). */
  label: ReactNode;
  /** Zugänglicher Name, falls `label` kein Text ist. */
  'aria-label'?: string;
  options?: RatingOption[];
  value: string | null;
  onValueChange?: (value: string) => void;
  /** `input` wählbar, `computed` zeigt das Ergebnis der Engine nur an. */
  variant?: 'input' | 'computed';
  /** Wert der Talent-Bar-Stufe (Unterstrich + Tooltip). */
  barValue?: string;
  trend?: 'up' | 'down' | null;
  /** Rechts: Kommentar-Icon, Aufklapp-Chevron (IconButtons). */
  actions?: ReactNode;
  /** Ohne eigene Kachel (wenn der Aufrufer schon eine Fläche stellt). */
  bare?: boolean;
  disabled?: boolean;
  className?: string;
  /** Inhalt unter der Reihe (abhängige Aussagen). */
  children?: ReactNode;
}

const pillClass = (selected: boolean) =>
  cn(
    'relative inline-flex h-[26px] shrink-0 items-center rounded-full px-3 text-body whitespace-nowrap select-none',
    'transition-colors duration-(--bar-dur-fast) ease-standard',
    'before:absolute before:inset-x-0 before:-inset-y-[7px] max-md:before:-inset-y-[9px]',
    selected ? 'bg-accent text-on-accent' : 'bg-pill-tile text-fg',
  );

function BarMarker() {
  return (
    <span
      aria-hidden="true"
      className="absolute inset-x-2 -bottom-1.5 h-0.5 rounded-full bg-chart-bar-line"
    />
  );
}

export function RatingPills({
  label,
  options = LEVEL_OPTIONS,
  value,
  onValueChange,
  variant = 'input',
  barValue,
  trend,
  actions,
  bare = false,
  disabled,
  className,
  children,
  ...aria
}: RatingPillsProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollChildIntoView(rowRef.current, '[data-selected="true"]');
  }, [value]);
  const barLabel = options.find((o) => o.value === barValue)?.label;
  const name = aria['aria-label'] ?? (typeof label === 'string' ? label : undefined);
  const hasExtras = Boolean(trend || actions);

  const renderPill = (option: RatingOption) => {
    const selected = option.value === value;
    const isBar = option.value === barValue;
    const content = (
      <>
        {option.label}
        {isBar ? <BarMarker /> : null}
        {isBar ? <span className="sr-only">, talent bar</span> : null}
      </>
    );
    const pill =
      variant === 'input' ? (
        <RadioGroup.Item
          key={option.value}
          value={option.value}
          data-selected={selected}
          className={cn(
            pillClass(selected),
            'hover:not-disabled:not-data-[state=checked]:bg-pill-tile-hover disabled:cursor-not-allowed',
          )}
        >
          {content}
        </RadioGroup.Item>
      ) : (
        <span
          key={option.value}
          role="radio"
          aria-checked={selected}
          tabIndex={selected ? 0 : -1}
          data-selected={selected}
          className={pillClass(selected)}
        >
          {content}
        </span>
      );
    return isBar && barLabel ? (
      <Tooltip key={option.value} content={`Talent bar: ${barLabel}`}>
        {pill}
      </Tooltip>
    ) : (
      pill
    );
  };

  const pillsClass =
    'bar-st-scroll-x flex items-center gap-2 overflow-x-auto py-2.5 max-md:-mx-4 max-md:px-4';
  const pills =
    variant === 'input' ? (
      <RadioGroup.Root
        ref={rowRef}
        value={value ?? ''}
        onValueChange={onValueChange}
        orientation="horizontal"
        loop={false}
        disabled={disabled}
        aria-label={name}
        className={pillsClass}
      >
        {options.map(renderPill)}
      </RadioGroup.Root>
    ) : (
      <div
        ref={rowRef}
        role="radiogroup"
        aria-readonly="true"
        aria-label={name}
        className={pillsClass}
      >
        {options.map(renderPill)}
      </div>
    );

  return (
    <div
      data-variant={variant}
      className={cn(!bare && 'rounded-xl bg-tile px-4 py-2.5', className)}
    >
      {/* Desktop: Label | Pills | Trend + Aktionen. Mobil: Label | Trend + Aktionen, Pills darunter (scrollen). */}
      <div
        className={cn(
          'grid min-h-14 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4',
          hasExtras && 'md:grid-cols-[minmax(0,1fr)_auto_auto]',
        )}
      >
        <span className="min-w-0 pt-1 text-label-strong text-fg md:pt-0">{label}</span>
        <div className="col-span-2 row-start-2 min-w-0 md:col-span-1 md:col-start-2 md:row-start-1">
          {pills}
        </div>
        {hasExtras ? (
          <div className="col-start-2 row-start-1 flex items-center gap-2 pt-1 md:col-start-3 md:pt-0">
            {trend ? (
              <span className={trend === 'up' ? 'text-success-fg' : 'text-danger-fg'}>
                {trend === 'up' ? (
                  <ArrowUpIcon size={16} aria-label="Trending up" />
                ) : (
                  <ArrowDownIcon size={16} aria-label="Trending down" />
                )}
              </span>
            ) : null}
            {actions ? <span className="flex shrink-0 items-center gap-1">{actions}</span> : null}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  );
}
